import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { DatePicker } from "antd-mobile";

import type { StaffDivideDetailData, StaffDivideListQuery, StaffDivideItem } from "../types";
import { DivideItem } from "./components/DivideItem";
import { getDivisionList, getDivisionTotal } from "@/api/staff";

const BRAND = "#FF6B35";

function fmtMoney(v: unknown): string {
  if (v === null || v === undefined) return "0.00";
  const n = typeof v === "string" ? Number(v) : (typeof v === "number" ? v : Number(v as any));
  if (Number.isFinite(n)) return n.toFixed(2);
  return String(v);
}

/** 通用列表分页 Hook（与 capital.tsx 类似） */
function useListPage<T>(
  fetchFn: (query: Record<string, unknown>) => Promise<T[]>,
  baseQuery: Record<string, unknown>,
) {
  const [data, setData] = useState<T[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState<"more" | "no-more" | "loading">("more");

  const init = async (reset = false) => {
    if (loading) return;
    const currentPage = reset ? 1 : page;
    setLoading(true);
    setLoadingStatus("loading");
    try {
      const list = (await fetchFn({ ...baseQuery, page: currentPage })) ?? [];
      setData((prev) => (reset ? list : [...prev, ...list]));
      if ((list.length ?? 0) < Number(baseQuery.limit || 10)) {
        setLoadingStatus("no-more");
      } else {
        setLoadingStatus("more");
        if (reset) setPage(1);
      }
    } catch (err) {
      console.error("[Divide List Error]", err);
      setLoadingStatus("no-more");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (page > 1) init(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  return { data, loading, loadingStatus, init, page, setPage, setData, setLoadingStatus };
}

export default function DivideDetailPage() {
  const navigate = useNavigate();
  const [moneyData, setMoneyData] = useState<StaffDivideDetailData | null>(null);
  const form = useRef<StaffDivideListQuery>({ limit: 10, page: 1 });
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [showStartPicker, setShowStartPicker] = useState(false);
  const [showEndPicker, setShowEndPicker] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const fetchDivideList = async (query: Record<string, unknown>): Promise<StaffDivideItem[]> => {
    const page = Number(query.page ?? 1);
    const limit = Number(query.limit ?? 10);
    const start_time = typeof query.start_time === "string" ? query.start_time : undefined;
    const end_time = typeof query.end_time === "string" ? query.end_time : undefined;

    const res = await getDivisionList({ page, limit, start_time, end_time });
    const list = Array.isArray((res as any)?.list) ? (res as any).list : [];
    return list as StaffDivideItem[];
  };

  const {
    data,
    loading,
    loadingStatus,
    init,
    setPage,
    setData,
    setLoadingStatus,
  } = useListPage<StaffDivideItem>(fetchDivideList, form.current as unknown as Record<string, unknown>);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const total = await getDivisionTotal();
        if (!alive) return;
        setMoneyData({
          total_price: fmtMoney(total.total_price),
          today_price: fmtMoney(total.today_price),
          item_price: fmtMoney(total.item_price),
          service_charge: fmtMoney(total.service_charge),
        });
      } catch {
        // 错误提示在 request 层统一处理
      }
    })();

    init(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (!e?.isIntersecting) return;
        if (loading) return;
        if (loadingStatus !== "more") return;
        setPage((p) => p + 1);
      },
      { root: null, rootMargin: "200px 0px 200px 0px", threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [loading, loadingStatus, setPage]);

  const handleSearch = () => {
    form.current.start_time = startTime;
    form.current.end_time = endTime;
    setLoadingStatus("more");
    setData([]);
    init(true);
  };

  const handleClear = () => {
    setStartTime("");
    setEndTime("");
    form.current.start_time = "";
    form.current.end_time = "";
    setLoadingStatus("more");
    setData([]);
    init(true);
  };

  return (
    <div className="min-h-screen pb-[calc(env(safe-area-inset-bottom,0px)+80px)]" style={{ background: "#F7F7F7", fontFamily: "'SF Pro Text','PingFang SC','Inter',system-ui,sans-serif" }}>
      {/* Header */}
      <div className="bg-white px-5 flex items-center gap-3" style={{ paddingTop: "calc(env(safe-area-inset-top,0px) + 16px)", paddingBottom: 13, borderBottom: "1px solid #F5F5F5" }}>
        <button onClick={() => navigate(-1)} className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer active:scale-[0.98] transition-transform shrink-0" style={{ background: "#F3F4F6" }}>
          <i className="ri-arrow-left-line" style={{ fontSize: 20, color: "#374151" }} />
        </button>
        <span className="text-lg font-bold text-gray-800">分成明细</span>
      </div>

      <div className="px-4 pt-4 space-y-3">
        {/* 统计卡片 */}
        <div className="bg-white rounded-2xl overflow-hidden p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1 pr-6 border-r border-gray-100">
              <span className="text-xs text-gray-500">总收益</span>
              <div className="text-2xl font-bold mt-1" style={{ color: "#EF4444" }}>
                ¥{moneyData?.total_price ?? "0.00"}
              </div>
            </div>
            <div className="flex-1 pl-6">
              <span className="text-xs text-gray-500">今日收益</span>
              <div className="text-2xl font-bold text-gray-900 mt-1">
                ¥{moneyData?.today_price ?? "0.00"}
              </div>
            </div>
          </div>
          {/* 底部统计 */}
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-center gap-12">
            <div className="text-center">
              <p className="text-xs text-gray-500">项目收益</p>
              <p className="text-sm font-bold text-gray-800 mt-1">¥{moneyData?.item_price ?? "0.00"}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500">提现手续费</p>
              <p className="text-sm font-bold text-gray-800 mt-1">¥{moneyData?.service_charge ?? "0.00"}</p>
            </div>
          </div>
        </div>

        {/* 筛选栏 */}
        <div className="bg-white rounded-2xl px-4 py-3 flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <button
                type="button"
                onClick={() => setShowStartPicker(true)}
                className="flex-1 min-w-0 py-2 px-3 rounded-xl bg-gray-50 text-sm text-left text-gray-700 truncate"
              >
                {startTime || "开始时间"}
                <i className="ri-calendar-2-line ml-2 text-gray-400" />
              </button>
              <button
                type="button"
                onClick={() => setShowEndPicker(true)}
                className="flex-1 min-w-0 py-2 px-3 rounded-xl bg-gray-50 text-sm text-left text-gray-700 truncate"
              >
                {endTime || "结束时间"}
                <i className="ri-calendar-2-line ml-2 text-gray-400" />
              </button>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={handleSearch} className="px-4 py-2 rounded-full text-xs font-semibold text-white active:scale-[0.98]" style={{ background: BRAND }}>搜索</button>
              <button onClick={handleClear} className="px-4 py-2 rounded-full text-xs font-semibold text-gray-600 bg-gray-100 active:scale-[0.98]">清除</button>
            </div>
          </div>

          {(startTime || endTime) && (
            <div className="text-xs text-gray-400">
              当前筛选：{startTime || "—"} ~ {endTime || "—"}
            </div>
          )}
        </div>

        {/* 列表 */}
        <div className="space-y-3 pb-4">
          {data.map((item) => (
            <DivideItem key={item.id} item={item} />
          ))}
          {!data.length && !loading && (
            <div className="pt-16 pb-8 text-center">
              <i className="ri-bar-chart-box-line text-5xl block mx-auto mb-3 text-gray-200" />
              <p className="text-sm text-gray-400">暂无分成记录</p>
            </div>
          )}
          {loading && (
            <div className="pt-4 text-center text-gray-400 text-xs">加载中...</div>
          )}
          {loadingStatus === "no-more" && data.length > 0 && (
            <div className="pb-4 text-center text-gray-300 text-xs">— 已经到底了 —</div>
          )}
          <div ref={loadMoreRef} style={{ height: 1 }} />
        </div>
      </div>

      {/* 日期选择器（底部弹出 + 结束时间不早于开始时间） */}
      <DatePicker
        visible={showStartPicker}
        onClose={() => setShowStartPicker(false)}
        max={endTime ? new Date(endTime) : new Date()}
        onConfirm={(d) => {
          const nextStart = d.toISOString().slice(0, 10);
          setStartTime(nextStart);
          if (endTime && endTime < nextStart) setEndTime(nextStart);
          setShowStartPicker(false);
        }}
      />
      <DatePicker
        visible={showEndPicker}
        onClose={() => setShowEndPicker(false)}
        min={startTime ? new Date(startTime) : undefined}
        max={new Date()}
        onConfirm={(d) => {
          const nextEnd = d.toISOString().slice(0, 10);
          if (startTime && nextEnd < startTime) setEndTime(startTime);
          else setEndTime(nextEnd);
          setShowEndPicker(false);
        }}
      />
    </div>
  );
}
